import React, { Fragment, useEffect, useState } from "react";
import { toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Box, Chip, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Select from "react-select";
import { ArrowLeft, UserPlus, Users, Award, Calendar, MapPin, Download, DollarSign, TrendingUp, AlertCircle, Printer, Edit, Eye, CheckCircle } from "react-feather";
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../layout/breadcrumb";
import TableSkeleton from "../../loader/TableSkeleton";
import DataNotFound from "../../DataNoteFound/DataNotFound";
import { MuiTabelStyle } from "../../utils/helper";
import { fetchEventRegistrations, fetchEventDetail, fetchEventRegistrationDetail, exportEventRegistrations, updateEventRegistration, reverifyEventPayment } from "../../api/Events/EventsApi";
import { FILE_API } from "../../api/Bank Master api/BankMasterAPi";
import { ERROR_CODE, BASE_URL_IMG } from "../../constant";
import LogOut from "../../utils/logout";
import { getEventPrintTemplate } from "./EventPrintHelper";

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventDetail, setEventDetail] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortOrder] = useState("DESC");
  const [filters, setFilters] = useState({ name: "", mobile: "", gender: "", village: "", city: "", startDate: "", endDate: "" });
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    newRegistrations: 0,
    existingRegistrations: 0,
    totalCollectedAmount: 0,
    totalPendingAmount: 0,
    totalFailedAmount: 0,
    dynamicSums: []
  });
  const [exportLoading, setExportLoading] = useState(false);

  // Detail Modal States
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editName, setEditName] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editGender, setEditGender] = useState('male');
  const [editCity, setEditCity] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editResponses, setEditResponses] = useState({});
  const [hasSplitName, setHasSplitName] = useState(false);

  // Shared style for filter bar inputs / selects
  const filterInputStyle = {
    height: "34px",
    padding: "0 10px",
    borderRadius: "6px",
    border: "1px solid #dee2e6",
    fontSize: "13px",
    backgroundColor: "#fff",
    outline: "none"
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '34px',
      height: '34px',
      minWidth: '160px',
      borderRadius: '6px',
      borderColor: '#dee2e6',
      fontSize: '13px',
      boxShadow: 'none',
      backgroundColor: '#fff',
      '&:hover': {
        borderColor: '#dee2e6'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 6px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'hidden'
    }),
    input: (base) => ({
      ...base,
      margin: '0px'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '32px'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0e7ff',
      borderRadius: '4px',
      margin: '1px',
      maxWidth: '120px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#4f46e5',
      fontSize: '11px',
      padding: '1px 4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#4f46e5',
      ':hover': {
        backgroundColor: '#c7d2fe',
        color: '#4338ca',
      },
    }),
  };

  const buildActiveFilters = () => {
    const active = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        if (Array.isArray(v)) {
          if (v.length > 0) active[k] = v;
        } else if (typeof v === 'string') {
          if (v.trim() !== '') active[k] = v.trim();
        } else {
          active[k] = v;
        }
      }
    });
    return active;
  };

  const handleClearFilters = () => {
    setFilters({ name: "", mobile: "", gender: "", village: "", city: "", startDate: "", endDate: "" });
  };


  const handleOpenDetailModal = async (registrationId) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    setDetailModalData(null);
    try {
      const { data } = await fetchEventRegistrationDetail(registrationId);
      if (data.status === 200) {
        const payloadData = data.payload.data;
        if (payloadData) {
          // Safely parse responses (handles double stringification)
          let parsedResponses = payloadData.responses;
          while (typeof parsedResponses === 'string') {
            try {
              parsedResponses = JSON.parse(parsedResponses);
            } catch (e) {
              parsedResponses = {};
              break;
            }
          }
          if (!parsedResponses || typeof parsedResponses !== 'object') {
            parsedResponses = {};
          }
          payloadData.responses = parsedResponses;

          // Safely parse responseSchemaSnapshot (handles double stringification)
          let parsedSnapshot = payloadData.responseSchemaSnapshot;
          while (typeof parsedSnapshot === 'string') {
            try {
              parsedSnapshot = JSON.parse(parsedSnapshot);
            } catch (e) {
              parsedSnapshot = [];
              break;
            }
          }
          if (!Array.isArray(parsedSnapshot)) {
            parsedSnapshot = [];
          }
          payloadData.responseSchemaSnapshot = parsedSnapshot;

          // Safely parse eventDetail.formFields if present
          if (payloadData.eventDetail && payloadData.eventDetail.formFields) {
            let fields = payloadData.eventDetail.formFields;
            while (typeof fields === 'string') {
              try {
                fields = JSON.parse(fields);
              } catch (e) {
                fields = [];
                break;
              }
            }
            if (Array.isArray(fields)) {
              payloadData.eventDetail.formFields = fields;
            }
          }
        }
        setDetailModalData(payloadData);
      } else {
        toast.error("Failed to load registration details");
        setDetailModalOpen(false);
      }
    } catch (error) {
      toast.error("Error loading registration details");
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenEditModal = async (registrationIdOrUuid) => {
    setEditLoading(true);
    setEditModalOpen(true);
    try {
      const { data } = await fetchEventRegistrationDetail(registrationIdOrUuid);
      if (data.status === 200) {
        const payloadData = data.payload.data;
        if (payloadData) {
          // Safely parse responses
          let parsedResponses = payloadData.responses;
          while (typeof parsedResponses === 'string') {
            try {
              parsedResponses = JSON.parse(parsedResponses);
            } catch (e) {
              parsedResponses = {};
              break;
            }
          }
          if (!parsedResponses || typeof parsedResponses !== 'object') {
            parsedResponses = {};
          }
          payloadData.responses = parsedResponses;

          // Safely parse responseSchemaSnapshot
          let parsedSnapshot = payloadData.responseSchemaSnapshot;
          while (typeof parsedSnapshot === 'string') {
            try {
              parsedSnapshot = JSON.parse(parsedSnapshot);
            } catch (e) {
              parsedSnapshot = [];
              break;
            }
          }
          if (!parsedSnapshot || !Array.isArray(parsedSnapshot)) {
            parsedSnapshot = [];
          }
          payloadData.responseSchemaSnapshot = parsedSnapshot;

          // Safely parse eventDetail.formFields if present
          if (payloadData.eventDetail && payloadData.eventDetail.formFields) {
            let fields = payloadData.eventDetail.formFields;
            while (typeof fields === 'string') {
              try {
                fields = JSON.parse(fields);
              } catch (e) {
                fields = [];
                break;
              }
            }
            if (Array.isArray(fields)) {
              payloadData.eventDetail.formFields = fields;
            }
          }

          setDetailModalData(payloadData);

          // Populate edit fields
          const isSplit = !!(parsedResponses.firstName || parsedResponses.firstname || parsedResponses.lastName || parsedResponses.lastname);
          setHasSplitName(isSplit);
          if (isSplit) {
            setEditFirstName(parsedResponses.firstName || parsedResponses.firstname || '');
            setEditLastName(parsedResponses.lastName || parsedResponses.lastname || '');
            setEditName('');
          } else {
            setEditName(payloadData.personDetail?.name || '');
            setEditFirstName('');
            setEditLastName('');
          }

          setEditMobile(payloadData.personDetail?.mobile || '');
          setEditGender(payloadData.personDetail?.gender || 'male');
          setEditCity(payloadData.personDetail?.city || '');
          setEditAddress(payloadData.personDetail?.address || '');
          setEditResponses(parsedResponses);
        }
      } else {
        toast.error("Failed to load registration details");
        setEditModalOpen(false);
      }
    } catch (error) {
      toast.error("Error loading registration details");
      setEditModalOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDynamicFieldChange = (fieldKey, val, fieldType) => {
    let cleanedVal = val;
    if (fieldType === 'number') {
      cleanedVal = val.replace(/[^0-9.]/g, '');
      const parts = cleanedVal.split('.');
      if (parts.length > 2) {
        cleanedVal = parts[0] + '.' + parts.slice(1).join('');
      }
    } else if (fieldType === 'phone') {
      cleanedVal = val.replace(/[^0-9\s+-]/g, '');
    }
    setEditResponses(prev => ({
      ...prev,
      [fieldKey]: cleanedVal
    }));
  };

  const handleSaveEdit = async () => {
    if (hasSplitName) {
      if (!editFirstName.trim()) {
        toast.error("Please enter First Name");
        return;
      }
      if (!editLastName.trim()) {
        toast.error("Please enter Last Name");
        return;
      }
    } else {
      if (!editName.trim()) {
        toast.error("Please enter Full Name");
        return;
      }
    }

    if (!editMobile || editMobile.trim() === '') {
      toast.error("Please enter Mobile Number");
      return;
    }

    // Prepare payload
    const cleanedResponses = { ...editResponses };
    if (detailModalData?.responseSchemaSnapshot) {
      detailModalData.responseSchemaSnapshot.forEach(f => {
        if (f.type === 'label') {
          delete cleanedResponses[f.fieldKey];
        }
      });
    }

    const payload = {
      registrationUuid: detailModalData.uuid,
      registrationId: detailModalData.registrationId,
      name: hasSplitName ? `${editFirstName.trim()} ${editLastName.trim()}`.trim() : editName.trim(),
      mobile: editMobile,
      gender: editGender,
      city: editCity,
      address: editAddress,
      responses: {
        ...cleanedResponses,
        gender: editGender,
        address: editAddress,
        city: editCity,
        ...(hasSplitName ? {
          firstname: editFirstName.trim(),
          lastname: editLastName.trim(),
          firstName: editFirstName.trim(),
          lastName: editLastName.trim()
        } : {})
      }
    };

    setEditLoading(true);
    try {
      const { data } = await updateEventRegistration(payload);
      if (data.status === 200) {
        toast.success(data.message || "Registration updated successfully!", {
          autoClose: 1500,
          transition: Zoom
        });
        setEditModalOpen(false);
        loadRegistrations();
      } else {
        toast.error(data.message || "Failed to update registration");
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || "Failed to update registration";
      toast.error(serverMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const renderEditDynamicField = (field) => {
    const fieldKey = field.fieldKey;
    const value = editResponses[fieldKey] || '';
    const isRequired = field.required;

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <Input
            type={field.type}
            className="form-control"
            placeholder={field.label}
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'phone':
        return (
          <Input
            type="tel"
            className="form-control"
            placeholder={field.label}
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value, 'phone')}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'number':
        return (
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="form-control"
            placeholder={field.label}
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value, 'number')}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'textarea':
        return (
          <Input
            type="textarea"
            rows="3"
            className="form-control"
            placeholder={field.label}
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'dropdown':
        return (
          <Input
            type="select"
            className="form-control"
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          >
            <option value="">Select Option</option>
            {field.options && field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </Input>
        );
      case 'radio':
        return (
          <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: '1.5rem' }}>
            {field.options && field.options.map((opt, idx) => (
              <FormGroup check key={idx} className="mb-0">
                <Label check style={{ cursor: 'pointer', fontWeight: '500' }}>
                  <Input
                    type="radio"
                    name={fieldKey}
                    value={opt}
                    checked={value === opt}
                    onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
                    required={isRequired}
                  />{' '}
                  {opt}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      case 'checkbox': {
        const checkedValues = Array.isArray(value) ? value : [];
        const handleCheckboxChange = (optValue, isChecked) => {
          let updated;
          if (isChecked) {
            updated = [...checkedValues, optValue];
          } else {
            updated = checkedValues.filter(v => v !== optValue);
          }
          handleDynamicFieldChange(fieldKey, updated);
        };
        return (
          <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: '1.5rem' }}>
            {field.options && field.options.map((opt, idx) => (
              <FormGroup check key={idx} className="mb-0">
                <Label check style={{ cursor: 'pointer', fontWeight: '500' }}>
                  <Input
                    type="checkbox"
                    value={opt}
                    checked={checkedValues.includes(opt)}
                    onChange={(e) => handleCheckboxChange(opt, e.target.checked)}
                  />{' '}
                  {opt}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      }
      case 'date':
        return (
          <Input
            type="date"
            className="form-control"
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'mapLocation':
        return (
          <Input
            type="text"
            className="form-control"
            placeholder="Coordinates (e.g. 21.1702, 72.8311)"
            value={value}
            onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
            required={isRequired}
            style={{ borderRadius: "8px" }}
          />
        );
      case 'file':
        const isUploaded = value && value !== '-';
        const fileUrl = isUploaded ? (value.startsWith('http') ? value : BASE_URL_IMG + value) : '';
        return (
          <div className="d-flex flex-column align-items-start w-100" style={{ gap: '0.5rem' }}>
            {isUploaded && (
              <div className="mb-2">
                <a href={fileUrl} target="_blank" rel="noreferrer">
                  <img
                    src={fileUrl}
                    alt={field.label}
                    style={{
                      maxWidth: '120px',
                      maxHeight: '80px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: '1px solid #ddd'
                    }}
                  />
                </a>
              </div>
            )}
            <Input
              type="file"
              className="form-control"
              onChange={async (e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  const uploadToast = toast.info("Uploading file...", { autoClose: false });
                  try {
                    let fd = new FormData();
                    fd.append('type', '5');
                    fd.append('file', files[0]);
                    const { data } = await FILE_API(fd);
                    if (data.status === 200 && data.payload?.imageUrl) {
                      handleDynamicFieldChange(fieldKey, data.payload.imageUrl);
                      toast.success("File uploaded successfully");
                    }
                  } catch (err) {
                    toast.error("Failed to upload file");
                  } finally {
                    toast.dismiss(uploadToast);
                  }
                }
              }}
              style={{ borderRadius: '8px' }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const handlePrintRegistrant = async (registrationId) => {
    const loadingToast = toast.info("Preparing print layout...", { autoClose: false });
    try {
      const { data } = await fetchEventRegistrationDetail(registrationId);
      if (data.status === 200) {
        const payloadData = data.payload.data;
        if (payloadData) {
          // Safely parse responses (handles double stringification)
          let parsedResponses = payloadData.responses;
          while (typeof parsedResponses === 'string') {
            try {
              parsedResponses = JSON.parse(parsedResponses);
            } catch (e) {
              parsedResponses = {};
              break;
            }
          }
          if (!parsedResponses || typeof parsedResponses !== 'object') {
            parsedResponses = {};
          }
          payloadData.responses = parsedResponses;

          // Safely parse responseSchemaSnapshot (handles double stringification)
          let parsedSnapshot = payloadData.responseSchemaSnapshot;
          while (typeof parsedSnapshot === 'string') {
            try {
              parsedSnapshot = JSON.parse(parsedSnapshot);
            } catch (e) {
              parsedSnapshot = [];
              break;
            }
          }
          if (!Array.isArray(parsedSnapshot)) {
            parsedSnapshot = [];
          }
          payloadData.responseSchemaSnapshot = parsedSnapshot;

          const printHtml = getEventPrintTemplate(eventDetail, payloadData);
          document.getElementById('donationReceipt').innerHTML = printHtml;
          toast.dismiss(loadingToast);
          setTimeout(() => {
            window.print();
          }, 300);
        }
      } else {
        toast.error("Failed to load registration details for print");
        toast.dismiss(loadingToast);
      }
    } catch (error) {
      toast.error("Error loading registration details for print");
      toast.dismiss(loadingToast);
    }
  };

  const handleVerifyPayment = async (registrationId) => {
    setLoading(true);
    try {
      const { data } = await reverifyEventPayment({ registration_id: registrationId });
      console.log('Event Payment Verified:', data);
      if (data.status === 200) {
        toast.success(data.message, {
          autoClose: 1500,
          transition: Zoom
        });
        loadEventDetailAndStats();
        loadRegistrations();
      } else {
        toast.error(data.message, {
          autoClose: 1500,
          transition: Zoom
        });
      }
    } catch (error) {
      console.error('Verify Event Payment Error:', error);
      toast.error(error?.response?.data?.message || "Error verifying payment", {
        autoClose: 1500,
        transition: Zoom
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrintFromModal = () => {
    if (!detailModalData) return;
    const printHtml = getEventPrintTemplate(eventDetail, detailModalData);
    document.getElementById('donationReceipt').innerHTML = printHtml;
    setTimeout(() => {
      window.print();
    }, 300);
  };

  const loadEventDetailAndStats = async () => {
    try {
      const { data } = await fetchEventDetail({ eventId: Number(eventId) });
      if (data.status === 200) {
        const foundEvent = data.payload.data;
        if (foundEvent) {
          let fields = foundEvent.formFields;
          while (typeof fields === 'string') {
            try {
              fields = JSON.parse(fields);
            } catch (e) {
              fields = [];
              break;
            }
          }
          if (!fields || !Array.isArray(fields)) {
            fields = [];
          }
          foundEvent.formFields = fields;
          setEventDetail(foundEvent);
        }
      }
    } catch (error) {
      console.error("Error loading event stats", error);
    }
  };

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      // Build search JSON only from non-empty filter values
      const activeFilters = buildActiveFilters();

      let paramsObj = {
        eventId: Number(eventId),
        pageNumber: page,
        recordsPerPage: pageSize,
        sortOrder,
        ...(Object.keys(activeFilters).length > 0 ? { search: JSON.stringify(activeFilters) } : {})
      };

      const { data } = await fetchEventRegistrations(paramsObj);
      if (data.status === 200) {
        setRegistrations(data.payload.data);
        setTotalRecords(data.pager.totalRecords);
        // Read registration summary counts from payload into single stats state
        setStats({
          totalRegistrations: data.payload.totalRegistrations || 0,
          newRegistrations: data.payload.newRegistrations || 0,
          existingRegistrations: data.payload.existingRegistrations || 0,
          totalCollectedAmount: data.payload.totalCollectedAmount || 0,
          totalPendingAmount: data.payload.totalPendingAmount || 0,
          totalFailedAmount: data.payload.totalFailedAmount || 0,
          dynamicSums: data.payload.dynamicSums || []
        });
      } else {
        setRegistrations([]);
      }
    } catch (error) {
      if (error?.response?.data?.status === ERROR_CODE.UNAUTHORIZED) {
        LogOut(navigate);
      }
      toast.error(error?.response?.data?.message || "Failed to load registrations", {
        autoClose: 1500,
        transition: Zoom
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEventDetailAndStats();
    loadRegistrations();
  }, [page, pageSize, sortOrder]);

  // Debounce filter changes: reset to page 1, then reload registrations
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadRegistrations();
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const defaultColumns = [
    {
      flex: 0.2,
      minWidth: 200,
      field: "name",
      headerName: "Registrant Name",
      renderCell: (params) => (
        <span className="font-weight-bold" style={{ color: "#2c3e50" }}>
          {params.value}
        </span>
      )
    },
    {
      flex: 0.15,
      minWidth: 150,
      field: "mobile",
      headerName: "Mobile Number"
    },
    {
      flex: 0.1,
      minWidth: 100,
      field: "gender",
      headerName: "Gender",
      renderCell: (params) => {
        const value = params.value?.toLowerCase();
        return (
          <Chip
            label={value === "male" ? "♂ Male" : "♀ Female"}
            color={value === "male" ? "primary" : "secondary"}
            size="small"
            variant="outlined"
            style={{ fontWeight: "600" }}
          />
        );
      }
    },
    {
      flex: 0.12,
      minWidth: 120,
      field: "village",
      headerName: "Village"
    },
    {
      flex: 0.12,
      minWidth: 120,
      field: "city",
      headerName: "City"
    },
    {
      flex: 0.25,
      minWidth: 220,
      field: "address",
      headerName: "Address",
      renderCell: (params) => (
        <Tooltip title={params.value || ""} placement="top-start" enterDelay={500}>
          <span style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            display: "inline-block",
            cursor: "pointer"
          }}>
            {params.value}
          </span>
        </Tooltip>
      )
    },
    {
      flex: 0.15,
      minWidth: 140,
      field: "isNewPerson",
      headerName: "Status",
      renderCell: (params) => {
        const isNew = params.value === true || params.value === 1 || params.value === '1';
        return (
          <span
            className={`badge ${isNew ? "badge-success" : "badge-info"} px-3 py-2 font-medium`}
            style={{ borderRadius: "20px", fontSize: "11px" }}
          >
            {isNew ? "🆕 New Person" : "👥 Existing Person"}
          </span>
        );
      }
    },
    {
      flex: 0.15,
      minWidth: 160,
      field: "createdAt",
      headerName: "Registration Date",
      renderCell: (params) => {
        return (
          <span style={{ fontSize: "12px", color: "#7f8c8d" }}>
            {new Date(params.value).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
          </span>
        );
      }
    },
    {
      flex: 0.15,
      minWidth: 160,
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <div className="d-flex align-items-center gap-2">
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleOpenDetailModal(params.row.uuid || params.row.id)}
              color="primary"
              size="small"
            >
              <Eye size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Registration">
            <IconButton
              onClick={() => handleOpenEditModal(params.row.uuid || params.row.id)}
              color="primary"
              size="small"
            >
              <Edit size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Form">
            <IconButton
              onClick={() => handlePrintRegistrant(params.row.uuid || params.row.id)}
              color="warning"
              size="small"
            >
              <Printer size={18} />
            </IconButton>
          </Tooltip>
          {eventDetail?.isPaymentEnabled && params.row.paymentStatus?.trim().toLowerCase() === 'pending' && (
            <Tooltip title="Verify Payment">
              <IconButton
                onClick={() => handleVerifyPayment(params.row.id)}
                color="success"
                size="small"
              >
                <CheckCircle size={18} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )
    }
  ];

  const columns = [...defaultColumns];
  const actionsCol = columns.pop();

  if (eventDetail?.isPaymentEnabled) {
    // Insert payment status and amount columns before actions
    columns.push(
      {
        flex: 0.12,
        minWidth: 120,
        field: "amount",
        headerName: "Amount Paid",
        renderCell: (params) => (
          <span className="font-weight-bold" style={{ color: "#27ae60" }}>
            ₹{parseFloat(params.value || 0).toFixed(2)}
          </span>
        )
      },
      {
        flex: 0.12,
        minWidth: 120,
        field: "paymentStatus",
        headerName: "Payment Status",
        renderCell: (params) => {
          const status = params.value?.toLowerCase() || 'free';
          let badgeColor = "badge-secondary";
          let label = status.toUpperCase();
          if (status === 'paid') {
            badgeColor = "badge-success";
            label = "🟢 PAID";
          } else if (status === 'pending') {
            badgeColor = "badge-warning text-dark";
            label = "🟡 PENDING";
          } else if (status === 'failed') {
            badgeColor = "badge-danger";
            label = "🔴 FAILED";
          } else if (status === 'free') {
            badgeColor = "badge-info";
            label = "🔵 FREE";
          }
          return (
            <span
              className={`badge ${badgeColor} px-3 py-2 font-medium`}
              style={{ borderRadius: "20px", fontSize: "11px" }}
            >
              {label}
            </span>
          );
        }
      },
      {
        flex: 0.15,
        minWidth: 160,
        field: "razorpay_payment_id",
        headerName: "Payment ID",
        renderCell: (params) => (
          <span className="text-muted" style={{ fontSize: "12px", fontFamily: "monospace" }}>
            {params.value || "-"}
          </span>
        )
      }
    );
  }

  // Insert dynamic columns (showInList)
  if (eventDetail?.formFields) {
    eventDetail.formFields.forEach(field => {
      if (field.showInList && field.fieldKey !== 'name_config') {
        columns.push({
          flex: 0.12,
          minWidth: 120,
          field: field.fieldKey,
          headerName: field.label,
          renderCell: (params) => {
            const val = params.row[field.fieldKey] !== undefined ? params.row[field.fieldKey] : params.row.responses?.[field.fieldKey];
            if (val === undefined || val === null) return '-';
            if (typeof val === 'object') {
              if (Array.isArray(val)) return val.join(', ');
              return JSON.stringify(val);
            }
            return String(val);
          }
        });
      }
    });
  }

  columns.push(actionsCol);

  const handleBack = () => {
    const layout = localStorage.getItem("layout") || "compact-wrapper";
    navigate(`${process.env.PUBLIC_URL}/Events/events/${layout}`);
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const activeFilters = buildActiveFilters();
      const paramsObj = {
        eventId: Number(eventId),
        ...(Object.keys(activeFilters).length > 0 ? { search: JSON.stringify(activeFilters) } : {})
      };
      const { data } = await exportEventRegistrations(paramsObj);

      // Convert response data to Blob — same pattern as incomeItem.jsx
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const eventName = eventDetail?.eventName
        ? eventDetail.eventName.replace(/[\\/:*?"<>|\s]+/g, '_')
        : `event-${eventId}`;
      link.download = `${eventName}-registrations.xlsx`;

      // Append to body, trigger download, then clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      toast.success('Registrations exported successfully!', { autoClose: 2000, transition: Zoom });
    } catch (error) {
      if (error?.response?.data?.status === ERROR_CODE.UNAUTHORIZED) {
        LogOut(navigate);
      }
      toast.error(error?.response?.data?.message || 'Failed to export registrations', {
        autoClose: 2000,
        transition: Zoom
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Events" title="Registration Details" />
      <Container fluid={true}>
        {/* Event Stats Card Headers */}
        {eventDetail && (
          <div className="mb-4">
            <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "16px", background: "linear-gradient(135deg, #7c3abf 0%, #4a1b8a 100%)", color: "white" }}>
              <Row className="align-items-center">
                <Col md="8">
                  <h2 className="font-weight-bold mb-2">{eventDetail.eventName}</h2>
                  <p className="mb-3 text-white-50" style={{ whiteSpace: 'pre-wrap' }}>{eventDetail.description || "No description provided."}</p>
                  <div className="d-flex flex-wrap gap-3" style={{ fontSize: "14px" }}>
                    {eventDetail.eventDate && (
                      <span className="d-flex align-items-center gap-1 bg-white bg-opacity-10 px-3 py-2 rounded-pill">
                        <Calendar size={16} className="text-warning" /> {eventDetail.eventDate}
                      </span>
                    )}
                    {eventDetail.location && (
                      <span className="d-flex align-items-center gap-1 bg-white bg-opacity-10 px-3 py-2 rounded-pill">
                        <MapPin size={16} className="text-warning" /> {eventDetail.location}
                      </span>
                    )}
                  </div>
                </Col>
                <Col md="4" className="text-md-end mt-3 mt-md-0">
                  <Button
                    color="light"
                    className="font-weight-bold d-inline-flex align-items-center gap-2"
                    onClick={handleBack}
                    style={{ borderRadius: "30px", padding: "10px 24px", color: "#fff", border: "1px solid #ffffff !important" }}
                  >
                    <ArrowLeft size={16} /> Back to Events
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Metrics cards */}
            <Row className="mt-4 g-3">
              <Col md="4">
                <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                  <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                    <div className="rounded-circle bg-primary bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                      <Award size={32} style={{color:"white"}} />
                    </div>
                    <h5 className="text-muted mb-1 font-weight-bold">Total Registrations</h5>
                    <h2 className="font-weight-extrabold text-dark m-0" style={{ fontSize: "36px" }}>{stats.totalRegistrations}</h2>
                  </CardBody>
                </Card>
              </Col>
              <Col md="4">
                <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                  <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                    <div className="rounded-circle bg-success bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                      <UserPlus size={32} style={{color:"white"}} />
                    </div>
                    <h5 className="text-muted mb-1 font-weight-bold">New Registrations</h5>
                    <h2 className="font-weight-extrabold text-success m-0" style={{ fontSize: "36px" }}>{stats.newRegistrations}</h2>
                  </CardBody>
                </Card>
              </Col>
              <Col md="4">
                <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                  <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                    <div className="rounded-circle bg-info bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                      <Users size={32} style={{color:"white"}} />
                    </div>
                    <h5 className="text-muted mb-1 font-weight-bold">Existing Registrations</h5>
                    <h2 className="font-weight-extrabold text-info m-0" style={{ fontSize: "36px" }}>{stats.existingRegistrations}</h2>
                  </CardBody>
                </Card>
              </Col>
              {Array.isArray(stats.dynamicSums) && stats.dynamicSums.map((sumItem) => (
                <Col md="4" key={sumItem.fieldKey}>
                  <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="rounded-circle bg-warning bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                        <TrendingUp size={32} style={{color:"white"}} />
                      </div>
                      <h5 className="text-muted mb-1 font-weight-bold" title={sumItem.label} style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minHeight: "48px"
                      }}>
                        Total {sumItem.label}
                      </h5>
                      <h2 className="font-weight-extrabold text-warning m-0" style={{ fontSize: "36px" }}>
                        {Number(sumItem.total).toLocaleString('en-IN')}
                      </h2>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
            {eventDetail.isPaymentEnabled && (
              <Row className="mt-4 g-3">
                <Col md="4">
                  <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="rounded-circle bg-success bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                        <TrendingUp size={32} style={{color:"#fff"}} />
                      </div>
                      <h5 className="text-muted mb-1 font-weight-bold">Collected Amount</h5>
                      <h2 className="font-weight-extrabold text-success m-0" style={{ fontSize: "28px" }}>
                        ₹{parseFloat(stats.totalCollectedAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="rounded-circle bg-warning bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                        <DollarSign size={32} style={{color:"#fff"}} />
                      </div>
                      <h5 className="text-muted mb-1 font-weight-bold">Pending Amount</h5>
                      <h2 className="font-weight-extrabold text-warning m-0" style={{ fontSize: "28px" }}>
                        ₹{parseFloat(stats.totalPendingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="border-0 shadow-sm h-100 text-center" style={{ borderRadius: "16px", overflow: "hidden" }}>
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="rounded-circle bg-danger bg-opacity-10 p-3 mb-3 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                        <AlertCircle size={32} style={{color:"#fff"}} />
                      </div>
                      <h5 className="text-muted mb-1 font-weight-bold">Failed Amount</h5>
                      <h2 className="font-weight-extrabold text-danger m-0" style={{ fontSize: "28px" }}>
                        ₹{parseFloat(stats.totalFailedAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </h2>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </div>
        )}

        {/* Registered Users List Table */}
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <CardHeader
                style={{
                  background: "#f8f9fa",
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start"
                }}
              >
                <div style={{ flex: 1 }}>
                  <h4 className="m-0 font-weight-bold" style={{ color: "#2c3e50" }}>
                    Registrants Directory
                  </h4>
                  <small className="text-muted">List of all individuals registered for this event</small>

                  {/* Filter row */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
                    <input
                      id="filter-reg-name"
                      type="text"
                      placeholder="Search name…"
                      value={filters.name}
                      onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                      style={filterInputStyle}
                    />
                    <input
                      id="filter-reg-mobile"
                      type="text"
                      placeholder="Search mobile…"
                      value={filters.mobile}
                      onChange={e => setFilters(f => ({ ...f, mobile: e.target.value }))}
                      style={filterInputStyle}
                    />
                    <select
                      id="filter-reg-gender"
                      value={filters.gender}
                      onChange={e => setFilters(f => ({ ...f, gender: e.target.value }))}
                      style={{ ...filterInputStyle, cursor: "pointer" }}
                    >
                      <option value="">All genders</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    <input
                      id="filter-reg-village"
                      type="text"
                      placeholder="Search village…"
                      value={filters.village}
                      onChange={e => setFilters(f => ({ ...f, village: e.target.value }))}
                      style={filterInputStyle}
                    />
                    <input
                      id="filter-reg-city"
                      type="text"
                      placeholder="Search city…"
                      value={filters.city}
                      onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                      style={filterInputStyle}
                    />
                    <input
                      id="filter-reg-start-date"
                      type="date"
                      placeholder="Start date"
                      value={filters.startDate}
                      onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                      style={filterInputStyle}
                    />
                    <input
                      id="filter-reg-end-date"
                      type="date"
                      placeholder="End date"
                      value={filters.endDate}
                      onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                      style={filterInputStyle}
                    />
                    
                    {eventDetail?.formFields && eventDetail.formFields
                      .filter(field => field.showInList === true && field.fieldKey !== 'name_config' && field.type !== 'label')
                      .map(field => {
                        if (field.type === 'dropdown') {
                          const options = (field.options || []).map(opt => ({ value: opt, label: opt }));
                          const selected = (filters[field.fieldKey] || []).map(val => ({ value: val, label: val }));
                          return (
                            <div key={field.fieldKey} style={{ minWidth: '160px', display: 'inline-block' }}>
                              <Select
                                isMulti
                                options={options}
                                value={selected}
                                onChange={(selectedOpts) => {
                                  const vals = selectedOpts ? selectedOpts.map(o => o.value) : [];
                                  setFilters(f => ({ ...f, [field.fieldKey]: vals }));
                                }}
                                placeholder={field.label}
                                styles={customSelectStyles}
                              />
                            </div>
                          );
                        }
                        if (field.type === 'number') {
                          return (
                            <input
                              key={field.fieldKey}
                              type="number"
                              placeholder={field.label}
                              value={filters[field.fieldKey] || ''}
                              onChange={e => setFilters(f => ({ ...f, [field.fieldKey]: e.target.value }))}
                              style={filterInputStyle}
                            />
                          );
                        }
                        return (
                          <input
                            key={field.fieldKey}
                            type="text"
                            placeholder={field.label}
                            value={filters[field.fieldKey] || ''}
                            onChange={e => setFilters(f => ({ ...f, [field.fieldKey]: e.target.value }))}
                            style={filterInputStyle}
                          />
                        );
                      })}

                    {Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v) && (
                      <button
                        id="btn-clear-filters"
                        onClick={handleClearFilters}
                        style={{ ...filterInputStyle, background: "#f1f3f4", color: "#5f6368", cursor: "pointer", border: "1px solid #dadce0", fontWeight: 500 }}
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                </div>
                <Button
                  id="btn-export-registrations"
                  color="success"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={exportLoading || registrations.length === 0}
                  className="d-inline-flex align-items-center gap-1 font-weight-bold"
                  style={{ borderRadius: "20px", padding: "8px 20px", gap: "6px" }}
                >
                  {exportLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: "14px", height: "14px" }} />
                  ) : (
                    <Download size={15} />
                  )}
                  {exportLoading ? " Exporting..." : " Export Excel"}
                </Button>
              </CardHeader>
              <div className="p-4">
                <Box
                  sx={{
                    "& .even": {
                      backgroundColor: "#fcfaff"
                    },
                    "& .odd": {
                      backgroundColor: "white"
                    }
                  }}
                >
                  {loading ? (
                    <TableSkeleton />
                  ) : registrations.length > 0 ? (
                    <DataGrid
                      sx={MuiTabelStyle}
                      autoHeight
                      pagination
                      rows={registrations}
                      rowCount={totalRecords}
                      pageSize={pageSize}
                      getRowId={(row) => row.id}
                      columns={columns}
                      page={page - 1}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                      onPageChange={(newPageNo) => setPage(newPageNo + 1)}
                      paginationMode="server"
                      disableSelectionOnClick
                      checkboxSelection={false}
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                      }
                    />
                  ) : (
                    <DataNotFound />
                  )}
                </Box>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Registration Details Modal */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(!detailModalOpen)} centered size="md">
        <ModalHeader toggle={() => setDetailModalOpen(!detailModalOpen)} className="font-weight-bold" style={{ color: "#4a1b8a" }}>
          Registration Details
        </ModalHeader>
        <ModalBody className="p-4">
          {detailLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Fetching registrant details...</p>
            </div>
          ) : detailModalData ? (
            <div>
              <h5 className="font-weight-bold text-primary border-bottom pb-2 mb-3">Personal Details</h5>
              <Row className="mb-4">
                {detailModalData.responses?.firstName || detailModalData.responses?.firstname ? (
                  <>
                    <Col xs="6" className="mb-2">
                      <span className="text-muted d-block" style={{ fontSize: "12px" }}>First Name</span>
                      <strong style={{ fontSize: "15px", color: "#2c3e50" }}>{detailModalData.responses.firstName || detailModalData.responses.firstname}</strong>
                    </Col>
                    <Col xs="6" className="mb-2">
                      <span className="text-muted d-block" style={{ fontSize: "12px" }}>Last Name</span>
                      <strong style={{ fontSize: "15px", color: "#2c3e50" }}>{detailModalData.responses.lastName || detailModalData.responses.lastname || "-"}</strong>
                    </Col>
                  </>
                ) : (
                  <Col xs="6" className="mb-2">
                    <span className="text-muted d-block" style={{ fontSize: "12px" }}>Full Name</span>
                    <strong style={{ fontSize: "15px", color: "#2c3e50" }}>{detailModalData.personDetail?.name || "-"}</strong>
                  </Col>
                )}
                <Col xs="6" className="mb-2">
                  <span className="text-muted d-block" style={{ fontSize: "12px" }}>Mobile Number</span>
                  <strong style={{ fontSize: "15px", color: "#2c3e50" }}>{detailModalData.personDetail?.mobile || "-"}</strong>
                </Col>
                <Col xs="6" className="mb-2 mt-2">
                  <span className="text-muted d-block" style={{ fontSize: "12px" }}>Gender</span>
                  <strong style={{ fontSize: "15px", color: "#2c3e50", textTransform: "capitalize" }}>{detailModalData.personDetail?.gender || "-"}</strong>
                </Col>
                <Col xs="6" className="mb-2 mt-2">
                  <span className="text-muted d-block" style={{ fontSize: "12px" }}>City</span>
                  <strong style={{ fontSize: "15px", color: "#2c3e50" }}>{detailModalData.personDetail?.city || "-"}</strong>
                </Col>
                <Col xs="12" className="mb-2 mt-2">
                  <span className="text-muted d-block" style={{ fontSize: "12px" }}>Address</span>
                  <strong style={{ fontSize: "14px", color: "#2c3e50" }}>{detailModalData.personDetail?.address || "-"}</strong>
                </Col>
              </Row>

              <h5 className="font-weight-bold text-primary border-bottom pb-2 mb-3 mt-4">Additional Submitted Responses</h5>
              {(() => {
                const fieldsToRender = (
                  (detailModalData?.eventDetail?.formFields && Array.isArray(detailModalData.eventDetail.formFields) && detailModalData.eventDetail.formFields.length > 0)
                    ? detailModalData.eventDetail.formFields
                    : (eventDetail?.formFields && Array.isArray(eventDetail.formFields) && eventDetail.formFields.length > 0)
                      ? eventDetail.formFields
                      : (detailModalData?.responseSchemaSnapshot || [])
                ).filter(field => field.fieldKey !== 'name_config' && field.type !== 'label');

                return (fieldsToRender.length === 0) ? (
                  <p className="text-muted italic">No custom fields were submitted for this registration.</p>
                ) : (
                  <Row>
                    {fieldsToRender.map((field) => {
                      // Case-insensitive response value lookup: field key may be lowercase (idprooffront) but response may differ
                      const responsesObj = detailModalData.responses || {};
                      const responsesLower = {};
                      Object.keys(responsesObj).forEach(k => { responsesLower[k.toLowerCase()] = responsesObj[k]; });
                      const val = responsesObj[field.fieldKey] !== undefined
                        ? responsesObj[field.fieldKey]
                        : responsesLower[field.fieldKey.toLowerCase()];

                      let displayVal = val;
                      let isMap = false;
                      let isFile = false;
                      let lat = null;
                      let lng = null;
                      let fileUrl = null;

                      if (field.type === 'file' && val && typeof val === 'string' && val !== '-') {
                        isFile = true;
                        // Build full URL — if already starts with http use as-is, otherwise prepend base URL
                        fileUrl = val.startsWith('http') ? val : BASE_URL_IMG + val;
                      } else if (field.type === 'mapLocation' && val) {
                        isMap = true;
                        if (typeof val === 'object') {
                          lat = val.lat;
                          lng = val.lng;
                        } else if (typeof val === 'string') {
                          if (val.trim().startsWith('{')) {
                            try {
                              const parsedVal = JSON.parse(val);
                              lat = parsedVal.lat;
                              lng = parsedVal.lng;
                            } catch (e) {}
                          }
                          if (!lat || !lng) {
                            const match = val.match(/^([-+]?[0-9]*\.?[0-9]+),\s*([-+]?[0-9]*\.?[0-9]+)$/);
                            if (match) {
                              lat = match[1];
                              lng = match[2];
                            }
                          }
                        }
                      } else if (field.type === 'checkbox' && Array.isArray(val)) {
                        displayVal = val.join(', ');
                      } else if (val === null || val === undefined || val === '') {
                        displayVal = "-";
                      }

                      return (
                        <Col xs="12" className="mb-3" key={field.fieldKey}>
                          <span className="text-muted d-block" style={{ fontSize: "12px" }}>{field.label}</span>
                          {isFile ? (
                            <div className="mt-1">
                              <a href={fileUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
                                <img
                                  src={fileUrl}
                                  alt={field.label}
                                  style={{
                                    maxWidth: '160px',
                                    maxHeight: '110px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '2px solid #e0d6f5',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<a href="${fileUrl}" target="_blank" class="btn btn-sm btn-outline-primary" style="border-radius:6px;font-size:12px">📎 View Document</a>`;
                                  }}
                                />
                              </a>
                              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                <a href={fileUrl} target="_blank" rel="noreferrer" style={{ color: '#7c3abf', textDecoration: 'underline' }}>
                                  Open full size ↗
                                </a>
                              </div>
                            </div>
                          ) : isMap ? (
                            <div className="mt-1 d-flex align-items-center gap-2">
                              <span className="badge badge-secondary py-2 px-3" style={{ fontSize: "12px" }}>
                                Lat: {lat || "-"}, Lng: {lng || "-"}
                              </span>
                              {lat && lng && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-info btn-xs text-white font-weight-bold px-2 py-1"
                                  style={{ fontSize: "11px", borderRadius: "4px", backgroundColor: "#02a4b4", border: "none" }}
                                >
                                  🗺️ Google Maps
                                </a>
                              )}
                            </div>
                          ) : (
                            <strong style={{ fontSize: "14px", color: "#2c3e50" }}>{String(displayVal)}</strong>
                          )}
                        </Col>
                      );
                    })}
                  </Row>
                );
              })()}
            </div>
          ) : (
            <p className="text-center text-muted">Failed to load registration details.</p>
          )}
        </ModalBody>
        <ModalFooter>
          {detailModalData && (
            <Button
              color="warning"
              onClick={handlePrintFromModal}
              style={{ borderRadius: "20px" }}
              className="text-white d-flex align-items-center gap-1"
            >
              <Printer size={16} /> Print Form
            </Button>
          )}
          <Button color="secondary" onClick={() => setDetailModalOpen(false)} style={{ borderRadius: "20px" }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Registration Details Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(!editModalOpen)} centered size="md">
        <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)} className="font-weight-bold" style={{ color: "#4a1b8a" }}>
          Edit Registration Details
        </ModalHeader>
        <ModalBody className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Fetching registrant details...</p>
            </div>
          ) : detailModalData ? (
            <div>
              <h5 className="font-weight-bold text-primary border-bottom pb-2 mb-3">Personal Details</h5>
              <Row>
                {hasSplitName ? (
                  <>
                    <Col md="6" className="mb-3">
                      <Label className="text-muted" style={{ fontSize: "12px" }}>First Name</Label>
                      <Input
                        type="text"
                        className="form-control"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label className="text-muted" style={{ fontSize: "12px" }}>Last Name</Label>
                      <Input
                        type="text"
                        className="form-control"
                        value={editLastName}
                        onChange={(e) => setEditLastName(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />
                    </Col>
                  </>
                ) : (
                  <Col md="12" className="mb-3">
                    <Label className="text-muted" style={{ fontSize: "12px" }}>Full Name</Label>
                    <Input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{ borderRadius: "8px" }}
                    />
                  </Col>
                )}
                <Col md="6" className="mb-3">
                  <Label className="text-muted" style={{ fontSize: "12px" }}>Mobile Number</Label>
                  <Input
                    type="text"
                    className="form-control"
                    value={editMobile}
                    onChange={(e) => setEditMobile(e.target.value.replace(/[^0-9\s+-]/g, ''))}
                    style={{ borderRadius: "8px" }}
                  />
                </Col>
                <Col md="6" className="mb-3">
                  <Label className="text-muted" style={{ fontSize: "12px" }}>Gender</Label>
                  <Input
                    type="select"
                    className="form-control"
                    value={editGender}
                    onChange={(e) => setEditGender(e.target.value)}
                    style={{ borderRadius: "8px" }}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </Input>
                </Col>
                <Col md="6" className="mb-3">
                  <Label className="text-muted" style={{ fontSize: "12px" }}>City</Label>
                  <Input
                    type="text"
                    className="form-control"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    style={{ borderRadius: "8px" }}
                  />
                </Col>
                <Col md="12" className="mb-3">
                  <Label className="text-muted" style={{ fontSize: "12px" }}>Address</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    className="form-control"
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                    style={{ borderRadius: "8px" }}
                  />
                </Col>
              </Row>

              <h5 className="font-weight-bold text-primary border-bottom pb-2 mb-3 mt-4">Additional Submitted Responses</h5>
              {(() => {
                const fieldsToRender = (
                  (detailModalData?.eventDetail?.formFields && Array.isArray(detailModalData.eventDetail.formFields) && detailModalData.eventDetail.formFields.length > 0)
                    ? detailModalData.eventDetail.formFields
                    : (eventDetail?.formFields && Array.isArray(eventDetail.formFields) && eventDetail.formFields.length > 0)
                      ? eventDetail.formFields
                      : (detailModalData?.responseSchemaSnapshot || [])
                ).filter(field => field.fieldKey !== 'name_config' && field.type !== 'label');

                return (fieldsToRender.length === 0) ? (
                  <p className="text-muted italic">No custom fields were submitted for this registration.</p>
                ) : (
                  <Row>
                    {fieldsToRender.map((field) => (
                      <Col xs="12" className="mb-3" key={field.fieldKey}>
                        <Label className="text-muted d-block" style={{ fontSize: "12px" }}>{field.label}</Label>
                        {renderEditDynamicField(field)}
                      </Col>
                    ))}
                  </Row>
                );
              })()}
            </div>
          ) : (
            <p className="text-center text-muted">Failed to load registration details.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSaveEdit}
            disabled={editLoading}
            style={{ borderRadius: "20px" }}
          >
            {editLoading ? "Saving..." : "Save Changes"}
          </Button>
          <Button color="secondary" onClick={() => setEditModalOpen(false)} style={{ borderRadius: "20px" }}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <div id="donationReceipt"></div>
    </Fragment>
  );
};

export default EventDetail;
